import { formatInTimeZone, zonedTimeToUtc } from 'date-fns-tz';
import { IANA_TIME_ZONE_MAP } from '../../../util/constants';

export const MFA_GRACE_PERIOD_TIME_ZONE = IANA_TIME_ZONE_MAP['US/Central'];
export const MFA_GRACE_PERIOD_TIME_ZONE_LABEL = 'Central Time';

const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

function parseDateOnly(value: string) {
    const match = DATE_ONLY_PATTERN.exec(value || '');
    if (!match) {
        return null;
    }

    const year = parseInt(match[1], 10);
    const month = parseInt(match[2], 10);
    const day = parseInt(match[3], 10);
    const date = new Date(Date.UTC(year, month - 1, day));

    if (
        date.getUTCFullYear() !== year ||
        date.getUTCMonth() !== month - 1 ||
        date.getUTCDate() !== day
    ) {
        return null;
    }

    return { year, month, day };
}

function addOneCalendarDay(value: string) {
    const parsed = parseDateOnly(value);
    if (!parsed) {
        return null;
    }

    const date = new Date(Date.UTC(parsed.year, parsed.month - 1, parsed.day + 1));
    const pad = (part: number) => part.toString().padStart(2, '0');
    return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
}

export function isGracePeriodDateOnly(value?: string | null) {
    return !!value && !!parseDateOnly(value);
}

export function getGracePeriodEnforcementDttmForDate(gracePeriodEndDate: string): string {
    const nextDate = addOneCalendarDay(gracePeriodEndDate);
    if (!nextDate) {
        throw new Error('Grace period end date must be a valid date.');
    }

    return zonedTimeToUtc(`${nextDate}T00:00:00`, MFA_GRACE_PERIOD_TIME_ZONE).toISOString();
}

export function getGracePeriodEnforcementDttm(value?: string | null): string | null {
    if (!value) {
        return null;
    }

    if (DATE_ONLY_PATTERN.test(value)) {
        if (!isGracePeriodDateOnly(value)) {
            return null;
        }
        return getGracePeriodEnforcementDttmForDate(value);
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return null;
    }

    return date.toISOString();
}

export function getGracePeriodEndDateForDisplay(value?: string | null): string | null {
    const enforcementDttm = getGracePeriodEnforcementDttm(value);
    if (!enforcementDttm) {
        return null;
    }

    return formatInTimeZone(
        new Date(new Date(enforcementDttm).getTime() - 1),
        MFA_GRACE_PERIOD_TIME_ZONE,
        'yyyy-MM-dd',
    );
}
